import { Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type TitleRow = {
  id: string;
  code: string;
  title: string;
  author?: string | null;
  available_count?: number;
};

type CopyRow = {
  id: string;
  accession_no: string;
  status: string;
  location_label?: string | null;
};

type ReservationForm = {
  student_id: string;
  title_id: string;
  preferred_copy_id?: string;
  notes?: string;
};

export default function LibraryReservationFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetStudent = searchParams.get('student_id') || undefined;
  const [titleQ, setTitleQ] = useState('');
  const [titleId, setTitleId] = useState<string | undefined>();
  const [form] = Form.useForm<ReservationForm>();

  const titles = useQuery({
    queryKey: ['education-library-titles-reservation', titleQ],
    queryFn: async () => {
      const res = await api.get<TitleRow[]>('/education/library/titles', {
        params: { q: titleQ || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const copies = useQuery({
    queryKey: ['education-library-copies-reservation', titleId],
    enabled: Boolean(titleId),
    queryFn: async () => {
      const res = await api.get<CopyRow[]>('/education/library/copies', {
        params: { title_id: titleId },
      });
      return (Array.isArray(res.data) ? res.data : []).filter(
        (copy) => copy.status !== 'lost' && copy.status !== 'withdrawn'
      );
    },
  });

  const create = useMutation({
    mutationFn: (values: ReservationForm) => api.post('/education/library/reservations', values),
    onSuccess: () => {
      message.success(t('libraryReservationCreated'));
      navigate('/education/library/reservations');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('newLibraryReservation')}
      description={t('newLibraryReservationIntro')}
      extra={
        <Button onClick={() => navigate('/education/library/reservations')}>
          {t('backToReservations')}
        </Button>
      }
    >
      <Card size="small" style={{ maxWidth: 680 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ student_id: presetStudent }}
          onFinish={create.mutate}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect style={{ width: '100%' }} disabled={Boolean(presetStudent)} />
          </Form.Item>
          <Form.Item name="title_id" label={t('title')} rules={[{ required: true }]}>
            <Select
              showSearch
              allowClear
              filterOption={false}
              placeholder={t('searchBookByName')}
              loading={titles.isFetching}
              onSearch={setTitleQ}
              options={(titles.data ?? []).map((row) => ({
                value: row.id,
                label: `${row.title}${row.author ? ` — ${row.author}` : ''} (${row.code}) · ${t('available')}: ${Number(row.available_count ?? 0)}`,
              }))}
              onChange={(value) => {
                setTitleId(value);
                form.setFieldValue('preferred_copy_id', undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="preferred_copy_id" label={t('preferredCopy')}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              disabled={!titleId}
              loading={copies.isFetching}
              placeholder={t('preferredCopyOptional')}
              options={(copies.data ?? []).map((copy) => ({
                value: copy.id,
                label: `${copy.accession_no} · ${t(copy.status)}${copy.location_label ? ` · ${copy.location_label}` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              {t('createReservation')}
            </Button>
            <Button onClick={() => navigate('/education/library/reservations')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
